import { IMemberProfile } from '@shared/shared/interfaces/member-profile.interfaces';
import * as memberProfileActions from '../actions';

export interface MemberProfileState extends IMemberProfile {
  loading: boolean;
  loaded: boolean;
}

export const initialState: MemberProfileState = {
  id: 0,
  member_id: 0,
  birth_date: '',
  created: '',
  email: '',
  full_name: '',
  first_name: '',
  last_name: '',
  preferred_contact_method: '',
  primary_lifestyle_manager: '',
  site: '',
  title: '',
  membership_card_number: '',
  concierge_briefings_enabled: false,
  url: '',
  errors: [{ error: '', property: '' }],
  travellers: [],
  phone_numbers: [],
  special_occasions: [],
  addresses: [],
  payment_cards: null,
  no_shows: 0,
  gender: '',
  citizenship: '',
  ten_opt_in: false,
  client_opt_in: false,
  loading: false,
  loaded: false,
  viewed_tour: <any>'',
  pay_with_points_card: null,
  flag_loyalty_enabled: false,
  new_traveller_added: false,
  available_requests: [],
  corporate_scheme_external_reference: '',
  external_reference: '',
};

export function reducer(
  state: MemberProfileState = initialState,
  action:
    | memberProfileActions.MemberProfileAction
    | memberProfileActions.MemberProfileTravellersAction
    | memberProfileActions.MemberProfileSpecialOccasionAction
    | memberProfileActions.MemberProfileAddressAction
    | memberProfileActions.MemberProfilePhoneNumberAction
    | memberProfileActions.MemberProfilePaymentCardAction
): MemberProfileState {
  /**
   * Loops through an array and sets all preferred values to false if passed value
   * has a preferred value of true. Used by the reducer when the user changes their preferred
   * phone number and address on the member profile page
   */
  function preferredArray<T>(newData: T, stateData: T[], fieldName: keyof T): T[] {
    return newData[fieldName] ? stateData.map((item) => ({ ...item, [fieldName]: false })) : stateData;
  }

  switch (action.type) {
    /**
     * Member profile
     */
    case memberProfileActions.VIEW_PROFILE: {
      return {
        ...state,
        loading: true,
      };
    }

    case memberProfileActions.VIEW_PROFILE_SUCCESS: {
      // get id number for each special occasion
      const newSpecialOccasions = action.payload.special_occasions.map((item) => ({
        ...item,
        id: parseInt(item.url.match(/\/(\d+)(?:.(?!\/\d+))+$/)[1], 10),
      }));

      return {
        ...action.payload,
        loading: false,
        loaded: true,
        special_occasions: [...state.special_occasions, ...newSpecialOccasions],
      };
    }

    case memberProfileActions.VIEW_PROFILE_FAIL: {
      return {
        ...state,
        loading: false,
        loaded: false,
      };
    }

    case memberProfileActions.UPDATE_EMAIL_SUCCESS: {
      return {
        ...state,
        email: action.payload.email,
      };
    }

    /**
     * Member profile travellers
     */

    case memberProfileActions.CREATE_PROFILE_TRAVELLER_SUCCESS: {
      return {
        ...state,
        travellers: [...state.travellers, action.payload],
        new_traveller_added: true,
      };
    }

    case memberProfileActions.DESTROY_PROFILE_TRAVELLER_SUCCESS: {
      return {
        ...state,
        travellers: [...state.travellers.filter((traveller) => traveller.id !== action.payload)],
      };
    }

    case memberProfileActions.UPDATE_PROFILE_TRAVELLER_SUCCESS: {
      return {
        ...state,
        travellers: state.travellers.map((traveller) =>
          traveller.id === action.payload.id ? action.payload : traveller
        ),
      };
    }

    /**
     * Member profile special occasions
     */

    case memberProfileActions.CREATE_PROFILE_SPECIAL_OCCASION_SUCCESS: {
      const id = parseInt(action.payload.url.match(/\/(\d+)(?:.(?!\/\d+))+$/)[1], 10);
      const newOccasion = { ...action.payload, id };

      return {
        ...state,
        special_occasions: [...state.special_occasions, newOccasion],
      };
    }

    case memberProfileActions.DESTROY_PROFILE_SPECIAL_OCCASION_SUCCESS: {
      return {
        ...state,
        special_occasions: state.special_occasions.filter((occasion) => occasion.id !== action.payload),
      };
    }

    case memberProfileActions.UPDATE_PROFILE_SPECIAL_OCCASION_SUCCESS: {
      const id = parseInt(action.payload.url.match(/\/(\d+)(?:.(?!\/\d+))+$/)[1], 10);
      const editedOccasion = { ...action.payload, id };

      return {
        ...state,
        special_occasions: state.special_occasions.map((occasion) =>
          occasion.id === editedOccasion.id ? editedOccasion : occasion
        ),
      };
    }

    /**
     * Member profile addresses
     */

    case memberProfileActions.CREATE_PROFILE_ADDRESS_SUCCESS: {
      const addresses = preferredArray(action.payload, state.addresses, 'preferred');

      return {
        ...state,
        addresses: [...addresses, action.payload],
      };
    }

    case memberProfileActions.DESTROY_PROFILE_ADDRESS_SUCCESS: {
      return {
        ...state,
        addresses: state.addresses.filter((address) => address.id !== action.payload),
      };
    }

    case memberProfileActions.UPDATE_PROFILE_ADDRESS_SUCCESS: {
      const addresses = preferredArray(action.payload, state.addresses, 'preferred');

      return {
        ...state,
        addresses: addresses.map((address) => (address.id === action.payload.id ? action.payload : address)),
      };
    }

    /**
     * Member profile payment cards
     */

    case memberProfileActions.GET_PROFILE_PAYMENT_CARDS_SUCCESS: {
      return {
        ...state,
        payment_cards: action.paymentCards,
      };
    }

    case memberProfileActions.CREATE_PROFILE_PAYMENT_CARD_SUCCESS: {
      const paymentCards = preferredArray(action.payload, state.payment_cards, 'is_default_card');

      return {
        ...state,
        payment_cards: [...paymentCards, action.payload],
      };
    }

    case memberProfileActions.DESTROY_PROFILE_PAYMENT_CARD_SUCCESS: {
      return {
        ...state,
        payment_cards: state.payment_cards.filter((paymentCard) => paymentCard.id !== action.payload),
      };
    }

    case memberProfileActions.UPDATE_PROFILE_PAYMENT_CARD_SUCCESS: {
      const paymentCards = preferredArray(action.payload, state.payment_cards, 'is_default_card');

      return {
        ...state,
        payment_cards: paymentCards.map((paymentCard) =>
          paymentCard.id === action.payload.id ? action.payload : paymentCard
        ),
      };
    }

    /**
     * Member profile phone numbers
     */

    case memberProfileActions.CREATE_PROFILE_PHONE_NUMBER_SUCCESS: {
      const phoneNumbers = preferredArray(action.payload, state.phone_numbers, 'preferred');

      return {
        ...state,
        phone_numbers: [...phoneNumbers, action.payload],
      };
    }

    case memberProfileActions.DESTROY_PROFILE_PHONE_NUMBER_SUCCESS: {
      return {
        ...state,
        phone_numbers: state.phone_numbers.filter((phoneNumber) => phoneNumber.id !== action.payload),
      };
    }

    case memberProfileActions.UPDATE_PROFILE_PHONE_NUMBER_SUCCESS: {
      const phoneNumbers = preferredArray(action.payload, state.phone_numbers, 'preferred');

      return {
        ...state,
        phone_numbers: phoneNumbers.map((number) => (number.id === action.payload.id ? action.payload : number)),
      };
    }

    case memberProfileActions.UPDATE_MEMBER_PROFILE:
      return {
        ...state,
        loaded: false,
        loading: true,
      };

    case memberProfileActions.UPDATE_MEMBER_PROFILE_SUCCESS:
      return {
        ...state,
        ...action.payload,
        loaded: true,
        loading: false,
      };

    case memberProfileActions.UPDATE_MEMBER_PROFILE_FAIL:
      return {
        ...state,
        errors: action.payload,
        loaded: false,
        loading: false,
      };

    case memberProfileActions.CLEAR_MEMBER_PROFILE:
      return {
        ...initialState,
      };
    default:
      return state;
  }
}

export const getUserId = (state: MemberProfileState) => state.id;
export const getLoading = (state: MemberProfileState) => state.loading;
export const getLoaded = (state: MemberProfileState) => state.loaded;
